import React from 'react';
import { Card, CardMedia, CardContent, Typography, Box, Chip, CardActionArea } from '@mui/material';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { NewsArticle } from '../../types';
import AdComponent from '../AdComponent';

interface NewsCardProps {
  article: NewsArticle;
  featured?: boolean;
  adCode?: string; // Opsiyonel reklam kodu
  showAdAtTop?: boolean; // Reklamın üstte mi yoksa altta mı gösterileceği
  showCategory?: boolean; // Kategoriyi göster/gizle
}

const NewsCard: React.FC<NewsCardProps> = ({
  article,
  featured = false,
  adCode,
  showAdAtTop = false,
  showCategory = true
}) => {
  // Eğer slug tanımlı değilse, id kullan
  const identifier = article.slug || article.id;
  const articleUrl = `/article/${identifier}`;

  // Format date to a readable format
  const formattedDate = moment(article.createdDate).format('DD MMMM YYYY HH:mm');

  // Reklam komponenti
  const adComponent = adCode ? (
    <Box sx={{ width: '100%', my: 1 }}>
      <AdComponent adCode={adCode} />
    </Box>
  ) : null;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(featured && {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.08)'
        })
      }}
    >
      {/* Eğer reklamın üstte gösterilmesi isteniyorsa */}
      {adCode && showAdAtTop && adComponent}
      
      <Link 
        to={articleUrl} 
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <CardActionArea sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height={featured ? 200 : 150}
            image={article.imageUrl || '/placeholder-news.jpg'}
            alt={`${article.title} - Haber Görseli`}
            loading="lazy"
            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
          />
          {article.category && showCategory && (
            <Chip
              label={article.category.name}
              size="small"
              color="primary"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: '0.7rem'
              }}
            />
          )}
          {featured && (
            <Chip
              label="Öne Çıkan"
              size="small"
              color="secondary"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 500,
                fontSize: '0.7rem'
              }}
            />
          )}
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            gutterBottom
            variant={featured ? 'h5' : 'h6'}
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {article.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {article.summary}
          </Typography>
          <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {formattedDate}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
      </Link>
      
      {/* Eğer reklamın altta gösterilmesi isteniyorsa */}
      {adCode && !showAdAtTop && adComponent}
    </Card>
  );
};

export default NewsCard; 